'use client';

import { useSession } from '@shared/auth';
import { Button } from '@shared/components/shadcn';
import { PLAY_ROUTE, useHoverRoutePrefetch } from '@shared/hooks';
import { Play } from '@shared/icons';
import Link from 'next/link';
import { BackgroundMedia } from '../../BackgroundMedia/BackgroundMedia';
import { FeaturedBanner } from '../../FeaturedBanner/FeaturedBanner';
import { FeaturedBannerLogoSection } from '../../FeaturedBannerLogoSection/FeaturedBannerLogoSection';
import { FeaturedBannerTitleSection } from '../../FeaturedBannerTitleSection/FeaturedBannerTitleSection';

type GameOfTheWeekProps = {
  href: string;
  video?: string;
  image?: string;
  logo: string;
  label?: string;
  title: string;
  gameName: string;
  playButtonText: string;
  className?: string;
  'data-testid'?: string;
  id: string;
};

function PlayButton({ playButtonText }: { playButtonText: string }) {
  return (
    <Button size="l" variant="primary" className="min-w-12 px-3 group-hover:px-6 group-hover:gap-2 mb-24 z-10 md:mb-15 transition-all duration-300" data-track-name="Play">
      <Play size="small" />
      <span className="whitespace-nowrap opacity-0 w-0 group-hover:opacity-100 group-hover:w-auto">{playButtonText}</span>
    </Button>
  );
}

function GameOfTheWeek(props: GameOfTheWeekProps) {
  const { href, video, image, logo, label, title, gameName, playButtonText, className, 'data-testid': dataTestId, id } = props;
  const {
    session: { status },
  } = useSession();
  const routePrefetch = useHoverRoutePrefetch({
    href: href,
    isAuthenticated: status === 'authenticated',
    authPathMatch: PLAY_ROUTE,
  });
  const LINK_CLASSES = 'dark relative group block size-full flex flex-col items-center pt-6 px-5';

  return (
    <FeaturedBanner className={className}>
      <Link
        href={href}
        {...routePrefetch}
        className={LINK_CLASSES}
        data-testid={dataTestId}
        data-track-container="Game Of The Week"
        data-track-game-name={gameName}
        data-track-game-id={id}
      >
        <BackgroundMedia video={video} image={image} gameName={gameName} />
        <FeaturedBannerTitleSection title={title} label={label} />
        <FeaturedBannerLogoSection logo={logo} gameName={gameName} />
        <PlayButton playButtonText={playButtonText} />
      </Link>
    </FeaturedBanner>
  );
}

export { GameOfTheWeek, PlayButton, type GameOfTheWeekProps };
